import type { SubscriptionData, SubscriptionUpdateForm } from 'chaika-frontend-types/frontend-cms';
import { SubscriptionStatus as EnumSubscriptionStatus } from 'chaika-enums';

import { SimpleList } from '@store/models/SimpleList';
import { DateHelper } from '@utils/DateHelper';
import { isDefined } from '@utils/isDefined';
import { translate } from '@chaika/i18n/helper';

export const SubscriptionStatus = {
    Archive: () => translate('archive'),
    Active: () => translate('activeFemSecond'),
    Planned: () => translate('planneFemSecond'),
};

interface FilterParams {
    isActual?: boolean;
}

interface ISubscription {
    startDate: Date | string;
    endDate: Date | string;
}

export class SubscriptionList extends SimpleList<SubscriptionData, FilterParams> {
    // @ts-ignore
    public update(id: string, updateParams: SubscriptionUpdateForm): void {
        const current = this.get(id);

        super.update(id, { ...current, ...updateParams } as SubscriptionData);
    }

    public hasPlanOverlap({
        id,
        planId,
        startDate: subStartDate,
        endDate: subEndDate,
    }: {
        id?: string;
        planId: string;
        startDate: string;
        endDate: string;
    }): boolean {
        const subscriptions = this.values.filter(({ planId: subPlanId }) => subPlanId === planId);
        return !subscriptions.every(
            ({ id: subId, startDate, endDate }) =>
                (id && id === subId) ||
                new Date() > new Date(endDate) ||
                new Date(subStartDate) > new Date(endDate) ||
                new Date(subEndDate) < new Date(startDate),
        );
    }

    public getActiveSubscriptions(): SubscriptionData[] {
        return this.values.filter(
            it => this.calculateSubscriptionStatus(it.startDate, it.endDate) === SubscriptionStatus.Active(),
        );
    }

    public calculateSubscriptionStatus(startDate: string, endDate: string) {
        const today = DateHelper.getStartOfDay();

        let status = '';

        if (new Date(endDate) < today) {
            status = SubscriptionStatus.Archive();
        } else if (new Date(startDate) <= today && new Date(endDate) >= today) {
            status = SubscriptionStatus.Active();
        } else if (new Date(startDate) > today) {
            status = SubscriptionStatus.Planned();
        }

        return status;
    }

    protected filterValue({ endDate }: SubscriptionData, params: FilterParams = {}): boolean {
        return isDefined(params?.isActual)
            ? params.isActual
                ? DateHelper.isAfter(endDate, new Date())
                : DateHelper.isBefore(endDate, new Date())
            : true;
    }
}

export const calculateStatus = ({ startDate, endDate }: ISubscription) => {
    const today = new Date();

    let status: EnumSubscriptionStatus;

    if (new Date(endDate) < today) {
        status = EnumSubscriptionStatus.Archived;
    } else if (new Date(startDate) <= today && new Date(endDate) >= today) {
        status = EnumSubscriptionStatus.Active;
    } else if (new Date(startDate) > today) {
        status = EnumSubscriptionStatus.Planned;
    } else {
        status = EnumSubscriptionStatus.Active;
    }

    return status;
};
